import config from "../config";
import {CollectionOrder, Outfit, OutfitProduct} from "./models";
import styles from './outfit-detail.scss';
import {getTranslationService, ITranslationService, Translator} from "../services/translation/translation-service";
import {getCountryService, ICountryService} from "../services/country/country-service";
import {Variant} from "../services/product/models";
import {getTrackingService} from "../services/tracking/tracking-service";
import {TrackClickEvent} from "../services/tracking/models";

enum Keys {
  ArrowLeft = 'ArrowLeft',
  ArrowRight = 'ArrowRight',
  Escape = 'Escape',
}

export class OutfitDetail extends HTMLElement {
  private root: ShadowRoot;
  private outfit: Outfit;
  private frame: string = "1_1";
  private collectionOrder: CollectionOrder;
  private translationService: ITranslationService;
  private translator: Translator;
  private countryService: ICountryService;

  private touchstartX: number = null;
  private touchstartY: number = null;

  constructor() {
    super();

    this.onKeyDown = this.onKeyDown.bind(this);
    this.touchstart = this.touchstart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);

    this.translationService = getTranslationService();
    this.countryService = getCountryService();
  }

  async connectedCallback() {
    window.addEventListener("ny-show-outfit-detail", this.show.bind(this));

    this.frame = this.getAttribute('frame') || this.frame;

    this.translator = await this.translationService.initTranslations('products', this.countryService.getCountryCodeFromLocation())

    this.root = this.attachShadow({mode: 'open'});
    this.root.innerHTML = `
      <style>${styles}</style>
      <div class="outfit-detail">
        <div class="container">
          <div class="header">
            <div class="icons">
              <div>
                <img class="btn prev" src="/img/icons/arrow-left.png" alt="previous" tabindex="0">
                <img class="btn next" src="/img/icons/arrow-right.png" alt="next" tabindex="0">
              </div>
              <div class="btn close-btn">
                <img class="close" src="/img/icons/close.png" alt="close" tabindex="0">
              </div>
            </div>
          </div>

          <div class="outfit-image"></div>
          <div class="products"></div>
        </div>
      </div>`;
    this.root.querySelector('.outfit-detail .header .close').addEventListener('click', this.hide.bind(this));
    this.root.querySelector('.outfit-detail .header .close').addEventListener('keydown', this.onKeyHide.bind(this));

    this.root.querySelector('.outfit-detail .header .prev').addEventListener('click', this.prev.bind(this));
    this.root.querySelector('.outfit-detail .header .prev').addEventListener('keydown', this.onKeyPrev.bind(this));

    this.root.querySelector('.outfit-detail .header .next').addEventListener('click', this.next.bind(this));
    this.root.querySelector('.outfit-detail .header .next').addEventListener('keydown', this.onKeyNext.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener("ny-show-outfit-detail", this.show.bind(this));
    this.root.querySelector('.outfit-detail .header .close').removeEventListener("click", this.hide.bind(this));
    this.root.querySelector('.outfit-detail .header .close').removeEventListener("keydown", this.onKeyHide.bind(this));

    this.root.querySelector('.outfit-detail .header .prev').removeEventListener("click", this.prev.bind(this));
    this.root.querySelector('.outfit-detail .header .prev').removeEventListener("keydown", this.onKeyPrev.bind(this));

    this.root.querySelector('.outfit-detail .header .next').removeEventListener("click", this.next.bind(this));
    this.root.querySelector('.outfit-detail .header .next').removeEventListener("keydown", this.onKeyNext.bind(this));
  }

  onKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case Keys.ArrowLeft:
        this.prev();
        break;
      case Keys.ArrowRight:
        this.next();
        break;
      case Keys.Escape:
        this.hide();
    }
  }

  show(event) {
    window.addEventListener('keydown', this.onKeyDown);
    this.root.querySelector('.outfit-image').addEventListener('touchstart', this.touchstart, {passive: true});
    this.root.querySelector('.outfit-image').addEventListener('touchend', this.handleTouchMove);

    this.outfit = event.detail.outfit;
    const clickedProductTrackingEvent = new TrackClickEvent({
      category: 'Lightbox',
      action: 'opened',
      label: `${this.outfit.position} - ${this.outfit.name}`
    })
    getTrackingService().trackClick(clickedProductTrackingEvent);
    this.render();
    this.root.querySelector('.outfit-detail').classList.add("active");
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }

  hide() {
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.querySelector('.outfit-image').removeEventListener('touchstart', this.touchstart);
    this.root.querySelector('.outfit-image').removeEventListener('touchend', this.handleTouchMove);

    const clickedProductTrackingEvent = new TrackClickEvent({
      category: 'Lightbox',
      action: 'closed',
      label: `${this.outfit.id} - ${this.outfit.name}`
    })
    getTrackingService().trackClick(clickedProductTrackingEvent);

    this.root.querySelector('.outfit-detail').classList.remove("active")
    document.body.removeAttribute('style');
    document.documentElement.removeAttribute('style');
  }

  onKeyNext(e: KeyboardEvent) {
    if(e.key == "Enter") {
      this.next();
    }
  }

  onKeyPrev(e: KeyboardEvent) {
    if(e.key == "Enter") {
      this.prev();
    }
  }

  onKeyHide(e: KeyboardEvent) {
    if(e.key == "Enter") {
      this.hide();
    }
  }

  next() {
    this.outfit = this.collectionOrder.getNextOutfit(this.outfit.id);
    const clickedProductTrackingEvent = new TrackClickEvent({
      category: 'Lightbox',
      action: 'clicked next',
      label: `${this.outfit.id} - ${this.outfit.name}`
    })
    getTrackingService().trackClick(clickedProductTrackingEvent);
    this.render();
  }

  prev() {
    this.outfit = this.collectionOrder.getPrevOutfit(this.outfit.id);
    const clickedProductTrackingEvent = new TrackClickEvent({
      category: 'Lightbox',
      action: 'clicked previous',
      label: `${this.outfit.id} - ${this.outfit.name}`
    })
    getTrackingService().trackClick(clickedProductTrackingEvent);
    this.render();
  }

  touchstart(evt: TouchEvent) {
    this.touchstartX = evt.changedTouches[0].clientX;
    this.touchstartY = evt.changedTouches[0].clientY;
  }

  handleTouchMove(evt: TouchEvent) {
    if (!this.touchstartX || !this.touchstartY) {
      return;
    }

    let xUp = evt.changedTouches[0].clientX;
    let yUp = evt.changedTouches[0].clientY;

    let xDiff = this.touchstartX - xUp;
    let yDiff = this.touchstartY - yUp;

    if (Math.abs(xDiff) > Math.abs(yDiff)) { // Most significant.
      if (xDiff > 0) {
        this.next()
      } else {
        this.prev()
      }
    }

    // Reset values.
    this.touchstartX = null;
    this.touchstartY = null;
  }

  render() {
    let outfitImageElement = this.root.querySelector('.outfit-image');
    outfitImageElement.innerHTML = null;
    let productsElement = this.root.querySelector('.products');
    productsElement.innerHTML = null;
    productsElement.classList.remove('no-products');
    const outfitNode = this.renderOutfitImage(this.outfit);
    outfitImageElement.appendChild(outfitNode);
    if (this.outfit && this.outfit.products && this.outfit.products.length > 0) {
      const productNodes = this.outfit.products.map(prod => this.renderProduct(prod));
      productNodes.forEach(n => productsElement.appendChild(n));
    } else {
      const noProductsElement = this.renderNoProducts();
      productsElement.classList.add('no-products');
      productsElement.appendChild(noProductsElement);
    }
  }

  handleOutfitImageLoaded(event) {
    const currentElement = event.target as HTMLImageElement;
    currentElement.classList.remove('loading');
  }

  handleProductImageLoaded(event) {
    const currentParent = event.target.closest('.product') as HTMLDivElement;
    currentParent.classList.remove('loading');
  }

  renderOutfitImage(outfit: Outfit): HTMLImageElement {
    const element = document.createElement('img');
    element.classList.add('loading');
    element.onload = this.handleOutfitImageLoaded;
    element.src = `${config.specialsApiUrl}/csp/images/image/public/${outfit.imageKey}?res=low&frame=${this.frame}`;
    element.alt = `outfit-image`;
    element.sizes = '(min-width: 1024px) 1200px, (max-width: 768px) 400px';
    element.srcset = `${config.specialsApiUrl}/csp/images/image/public/${outfit.imageKey}?res=full-hd&frame=${this.frame} 1200w,
                      ${config.specialsApiUrl}/csp/images/image/public/${outfit.imageKey}?res=mid&frame=${this.frame} 400w`
    return element;
  }

  renderProduct(outfitProduct: OutfitProduct): HTMLAnchorElement {
    const product = outfitProduct.product;
    let variant: Variant = null;
    if (product.variants.length === 0) {
      return;
    } else if (product.variants.length === 1) {
      variant = product.variants[0];
    } else {
      variant = product.getVariant(outfitProduct.product_id, outfitProduct.variant_id)
    }
    const imgKey = variant.getCutout()
    const label = product.getLabel(this.translator);

    const productA = document.createElement('a');
    productA.href = `/products/detail/${variant.productId}/${variant.id}?gender=${product.customerGroup}`;
    productA.target = '_blank';
    productA.onclick = () => this.trackProductClick(variant);
    const productDIV = document.createElement('div');
    productDIV.classList.add('product');
    productDIV.classList.add('loading');
    const imageDIV = document.createElement('div');
    imageDIV.classList.add('image');
    const img = document.createElement('img');
    img.onload = this.handleProductImageLoaded;
    img.src = `${config.specialsApiUrl}/csp/images/image/public/${imgKey}?res=low`;
    img.alt = `${label}`;
    img.sizes = '(min-width: 1024px) 800px, (max-width: 768px) 400px';
    img.srcset = `${config.specialsApiUrl}/csp/images/image/public/${imgKey}?res=high 800w,
                  ${config.specialsApiUrl}/csp/images/image/public/${imgKey}?res=mid 400w`
    const infoDIV = document.createElement('div');
    infoDIV.classList.add('info');
    const labelDIV = document.createElement('div');
    labelDIV.classList.add('label');
    labelDIV.innerHTML = `${label}`;
    const priceDIV = document.createElement('div');
    priceDIV.classList.add('price');
    const currentPriceDIV = document.createElement('span');
    currentPriceDIV.innerHTML = `${variant.getCurrentPrice(this.countryService['currentCountry'].iso_639, this.countryService['currentCountry'].iso_3166)}`;

    infoDIV.appendChild(labelDIV);

    if(variant?.originalExchangePrice && !variant.comingSoon) {
      const currentExchangePriceDIV = document.createElement('span');
      currentExchangePriceDIV.classList.add('current-exchange-price');
      currentExchangePriceDIV.innerHTML = `${variant?.getCurrentExchangePrice(this.countryService['currentCountry'].iso_639, this.countryService['currentCountry'].iso_3166)}`;
      priceDIV.appendChild(currentPriceDIV);
      priceDIV.appendChild(currentExchangePriceDIV);
      priceDIV.classList.add('dual-price-display-styles');
    } else {
      priceDIV.appendChild(currentPriceDIV);
      priceDIV.classList.remove('dual-price-display-styles');
    }

    infoDIV.appendChild(priceDIV);
    imageDIV.appendChild(img);
    productDIV.appendChild(imageDIV);
    productDIV.appendChild(infoDIV);
    productA.appendChild(productDIV);

    return productA;
  }

  trackProductClick(variant: Variant) {
    const clickedProductTrackingEvent = new TrackClickEvent({
      category: 'Lightbox',
      action: 'clicked product',
      label: `${variant.productId} - ${variant.id}`,
      isSale: variant.sale,
      imageType: 'CUTOUT'
    })
    getTrackingService().trackClick(clickedProductTrackingEvent);
  }

  renderNoProducts(): HTMLDivElement {
    let divElement = document.createElement('div');
    divElement.innerHTML = this.translator.translate('lightbox.no-products');

    return divElement
  }

  setCollectionOrder(collectionOrder: CollectionOrder) {
    this.collectionOrder = collectionOrder
  }

}

window.customElements.define('outfit-detail', OutfitDetail);
